import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug, fetchAccommodations } from '../lib/cms';
import Gallery from '../components/Gallery';
import SiteMapViewer from '../components/SiteMapViewer';

const formatPrice = (priceStr) => {
  if (!priceStr) return '';
  // Remove "per nat", "pr. nat", "/ nat", "/nat", "per natt" etc (case insensitive)
  let cleaned = priceStr.replace(/\s*(per|pr\.?|\/)\s*nat(t)?/gi, '').trim();
  
  // If it's a numeric price like "fra 3.500 DKK" but doesn't have ",00", let's make sure it has ",00"
  if (cleaned.includes('DKK') && !cleaned.includes(',00') && !cleaned.includes(',')) {
    cleaned = cleaned.replace(/(\d+[\.\d]*)\s*DKK/g, '$1,00 DKK');
  }
  return cleaned;
};

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [featuredAccs, setFeaturedAccs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    async function loadData() {
      const page = await fetchPageBySlug('home');
      setPageData(page);

      const allAccs = await fetchAccommodations();
      setTotalCount(allAccs.length);
      // Filter out Klubhuset (event) and get 3 featured holiday homes/cabins
      const filtered = allAccs.filter(a => a.slug?.da !== 'klubhuset').slice(0, 3);
      setFeaturedAccs(filtered);
    }
    loadData();
  }, []);

  const metaTitle = pageData?.meta_title?.da || "Campen Camping – Feriehuse, Hytter, Campingvogne. God stemning og havudsigt.";
  const metaDesc = pageData?.meta_description?.da || "Hos Campen Camping på Samsø kan du leje hytter, feriehuse, campingvogne og teltpladser eller leje hele pladsen til events og selskaber.";

  return (
    <div className="animate-fade-in">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-[#e8f3fa] to-white text-stone-800 py-24 md:py-32 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/hyt14-scaled.jpg"
            alt="Campen Camping Samsø"
            className="w-full h-full object-cover opacity-15 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-6xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center text-center lg:text-left">
          {/* Billede - øverst på mobil, til højre på PC */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end order-1 lg:order-2">
            <div className="relative group">
              {/* Dekorativ baggrunds-accent bag billedet på PC */}
              <div className="absolute -inset-2 rounded-[2rem] bg-gradient-to-tr from-[#67aedd]/20 to-transparent opacity-0 lg:opacity-100 blur-lg group-hover:scale-105 transition-transform duration-500" />
              
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/forside-campen-camping.jpg"
                alt="Campen Camping"
                className="w-72 md:w-96 lg:w-[420px] xl:w-[460px] h-auto rounded-3xl shadow-xl object-cover border-4 border-white relative z-10 transition-transform duration-500 hover:scale-[1.02]"
              />
              <span className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-xs text-[#5599cc] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full border border-[#67aedd]/30 shadow-md whitespace-nowrap flex items-center z-20">
                <i className="fa-solid fa-star mr-1.5 text-[#67aedd]" /> Sydsamsøs perle
              </span>
            </div>
          </div>

          {/* Tekstindhold - nederst på mobil, til venstre på PC */}
          <div className="lg:col-span-7 flex flex-col items-center lg:items-start order-2 lg:order-1">
            <h1 className="text-4xl md:text-5xl lg:text-5.5xl xl:text-6xl font-serif font-bold leading-tight text-stone-900">
              Feriehuse, Hytter <br className="hidden lg:inline" />
              & Event <br className="hidden md:inline" />
              <span className="text-[#67aedd] block mt-1">God stemning og havudsigt</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-stone-600 max-w-2xl leading-relaxed">
              Velkommen til Campen Camping – din fredelige oase på Samsø med egen badebro, vidunderlige solnedgange og plads til hele familien eller det store selskab.
            </p>
          </div>
        </div>
      </section>

      {/* The Two Main Tracks (Hovedspor) */}
      <section className="py-16 bg-stone-50 border-b border-stone-200/60">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0f0d0e]">
              Vælg din ferieform på Samsø
            </h2>
            <p className="text-stone-600 mt-3">
              Uanset om du søger en hyggelig parferie i en hytte eller vil leje hele pladsen til et stort event, har vi rammerne.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Track 1: Cabins & Holiday Homes */}
            <Link
              to="/feriehuse?type=Feriehus,Hytte"
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md border border-stone-200/50 transition-all duration-300 flex flex-col h-full group/card cursor-pointer"
            >
              <div className="h-56 overflow-hidden relative">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/d88001-main-01.jpg"
                  alt="Feriehuse og hytter"
                  className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4 bg-[#0f0d0e] text-white text-xs font-bold px-3.5 py-1.5 rounded-full">
                  2-24 personer
                </div>
              </div>
              <div className="p-6 flex-grow flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-serif font-bold text-[#0f0d0e] mb-2 group-hover/card:text-[#67aedd] transition-colors">
                    Feriehuse & Hytter
                  </h3>
                  <p className="text-stone-600 text-sm leading-relaxed mb-4">
                    Lej et af vores rummelige feriehuse som Hammerslaget med plads til 18 personer, eller vælg en af vores hyggelige retro-hytter. Perfekt til parferie, familieferie eller en hyggelig weekend på øen.
                  </p>
                </div>
                <span
                  className="text-[#67aedd] group-hover/card:text-[#5aa1cc] font-bold text-sm flex items-center gap-2 self-start"
                >
                  Se alle feriehuse <i className="fa-solid fa-arrow-right group-hover/card:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>

            {/* Track 2: Caravans & Tent Sites */}
            <Link
              to="/feriehuse?type=Campingvogn,Plads"
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md border border-stone-200/50 transition-all duration-300 flex flex-col h-full group/card cursor-pointer"
            >
              <div className="h-56 overflow-hidden relative">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/t4_1.jpg"
                  alt="Autocamper, Campingvogn & Teltpladser"
                  className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4 bg-[#67aedd] text-white text-xs font-bold px-3.5 py-1.5 rounded-full">
                  Natur & Camping
                </div>
              </div>
              <div className="p-6 flex-grow flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-serif font-bold text-[#0f0d0e] mb-2 group-hover/card:text-[#67aedd] transition-colors">
                    Autocamper, Campingvogn & Teltpladser
                  </h3>
                  <p className="text-stone-600 text-sm leading-relaxed mb-4">
                    Oplev den ægte campingsjæl. Book en af vores hyggelige retro-campingvogne, eller tag eget telt/autocamper med og find den perfekte plads tæt på vandet med adgang til pladsens faciliteter.
                  </p>
                </div>
                <span
                  className="text-[#67aedd] group-hover/card:text-[#5aa1cc] font-bold text-sm flex items-center gap-2 self-start"
                >
                  Se campingmuligheder <i className="fa-solid fa-arrow-right group-hover/card:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>

            {/* Track 3: Rent the Camp */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md border border-stone-200/50 transition-all duration-300 flex flex-col h-full group/card">
              <div className="h-56 overflow-hidden relative">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/3.jpeg"
                  alt="Lej hele pladsen"
                  className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4 bg-[#0f0d0e] text-white text-xs font-bold px-3.5 py-1.5 rounded-full">
                  Events & Selskaber
                </div>
              </div>
              <div className="p-6 flex-grow flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-serif font-bold text-[#0f0d0e] mb-2 group-hover/card:text-[#67aedd] transition-colors">
                    Lej hele Campen
                  </h3>
                  <p className="text-stone-600 text-sm leading-relaxed mb-4">
                    Skal du holde bryllup, familietræf, virksomhedsarrangement eller kurser? Du kan leje hele pladsen med plads til ca. 100 spisende i det charmerende klubhus og ca. 100 overnattende gæster.
                  </p>
                </div>
                <Link
                  to="/lej-campen"
                  className="text-[#67aedd] hover:text-[#5aa1cc] font-bold text-sm flex items-center gap-2 group self-start"
                >
                  Læs om leje af pladsen <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rolig Samsø-stemning (Verbatim Scraped Text) */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img
              src="https://assets.auxodev.dk/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/image34-scaled.jpeg"
              alt="Samsø natur og camping"
              className="rounded-2xl shadow-md w-full object-cover aspect-4/3"
            />
            <div className="absolute -bottom-6 -right-6 bg-[#67aedd] text-white p-6 rounded-2xl shadow-lg hidden sm:block max-w-xs">
              <p className="font-serif font-bold text-lg leading-snug text-white">
                "Pladsen har sin egen badebro og er berømt for sine vidunderlige solnedgange."
              </p>
            </div>
          </div>

          <div className="flex flex-col justify-center">
            <span className="text-[#67aedd] text-xs font-bold uppercase tracking-wider mb-2">
              Fred, ro og ugenerte rammer
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0f0d0e] mb-6 leading-tight">
              Oplev den ægte ø-stemning på Sydsamsø
            </h2>
            <div className="text-stone-600 text-sm md:text-base space-y-4 leading-relaxed">
              <p>
                Samsø er en charmerende ferieø midt i Danmark, hvor livet går lidt langsommere, og naturen får lov at fylde. Her kan man cykle gennem de små landsbyer, nyde lokale råvarer, bade ved de rene strande og opleve den afslappede østemning, der gør Samsø til et perfekt sted at koble helt af.
              </p>
              <p>
                Øens sydligste campingplads er smukt beliggende med en overdådig havudsigt mod vest. I det fjerne ses Endelave og Horsens Fjord, mens man mod nord kan se helt til Århus.
              </p>
              <p>
                Pladsen er meget fredelig og hyggelig, og der er god plads til den enkelte campist. Der er også mulighed for at samles i flok, hvis man f.eks er en gruppe, der er på en fælles tur. Der er tradition for, at man vælger pladsen her til biltræf, golfture eller i forbindelse med kursusvirksomhed.
              </p>
              <p>
                Der er fine fiskeforhold ud for pladsen. Der fiskes både sild og ørred og i sæsonen er der hornfisk og hummere i det rene vand.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Accommodations */}
      <section className="py-20 bg-stone-50 border-t border-b border-stone-200/60">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex flex-col sm:flex-row items-end justify-between mb-12 gap-4">
            <div>
              <span className="text-[#67aedd] text-xs font-bold uppercase tracking-wider">
                Udlejning
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0f0d0e] mt-1">
                Udvalgte feriehuse & hytter
              </h2>
            </div>
            <Link
              to="/feriehuse"
              className="bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-6 py-3 rounded-full shadow-sm transition-all duration-300 text-sm whitespace-nowrap"
            >
              Se alle enheder ({totalCount})
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredAccs.map((acc, index) => {
              const mainImg = acc.images?.[0] || 'https://assets.auxodev.dk/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/d88001-main-01.jpg';
              // Optimize image size
              const optimizedImg = mainImg.replace('https://assets.auxodev.dk/assets', 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets');

              return (
                <div
                  key={acc.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md border border-stone-200/50 transition-all duration-300 flex flex-col h-full"
                >
                  <div className="h-56 overflow-hidden relative">
                    <img
                      src={optimizedImg}
                      alt={acc.title?.da || acc.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-white/90 text-[#0f0d0e] text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                      <i className="fa-solid fa-users mr-1" /> {acc.capacity} pers.
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-xs font-bold text-[#67aedd] uppercase tracking-wider">
                          {acc.type?.da || acc.type}
                        </span>
                        {acc.size && (
                          <span className="text-xs text-stone-500">
                            {acc.size}
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl font-serif font-bold text-[#0f0d0e] mb-2">
                        {acc.title?.da || acc.title}
                      </h3>
                      <p className="text-stone-600 text-xs leading-relaxed line-clamp-3 mb-4" dangerouslySetInnerHTML={{ __html: acc.description?.da || acc.description }} />
                    </div>
                    <div className="pt-4 border-t border-stone-100 flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-stone-500">
                        {formatPrice(acc.price_info?.da || acc.price_info)}
                      </span>
                      <Link
                        to={`/feriehuse?valgt=${acc.slug?.da || acc.slug}`}
                        className="text-[#67aedd] hover:text-[#5aa1cc] font-bold text-xs flex items-center gap-1 group"
                      >
                        Vis detaljer <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* General Camp Gallery */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-[#67aedd] text-xs font-bold uppercase tracking-wider">
              Billeder
            </span>
            <h2 className="text-3xl font-serif font-bold text-[#0f0d0e] mt-1">
              Stemningsbilleder fra pladsen
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Klik på billederne for at se dem i stor visning.
            </p>
          </div>
          <Gallery slug="campen-generelt" />
        </div>
      </section>

      {/* Location & Map */}
      <section className="py-16 bg-stone-50 border-t border-stone-200/60">
        <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[#67aedd] text-xs font-bold uppercase tracking-wider">
              Beliggenhed
            </span>
            <h2 className="text-3xl font-serif font-bold text-[#0f0d0e] mt-1 mb-6">
              Find os på Sydsamsø
            </h2>
            <p className="text-stone-600 text-sm md:text-base leading-relaxed mb-6">
              Vores campingplads er smukt beliggende på den sydligste del af Samsø omgivet af den skønneste natur. Tæt på havet med egen badebro og kun 400 meter til havnen i Kolby Kås. Her kan du nyde uforstyrret ro og fantastiske solnedgange over vandet.
            </p>
            <div className="bg-white p-6 rounded-2xl border border-stone-200/50 shadow-sm flex flex-col gap-4">
              <div className="flex items-start gap-4">
                <div className="bg-[#67aedd]/10 text-[#67aedd] p-3 rounded-xl">
                  <i className="fa-solid fa-map-location-dot text-lg" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-[#0f0d0e]">Adresse</h4>
                  <p className="text-stone-600 text-sm mt-1">Vestermarksvej 72, 8305 Samsø</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-[#67aedd]/10 text-[#67aedd] p-3 rounded-xl">
                  <i className="fa-solid fa-compass text-lg" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-[#0f0d0e]">Seværdigheder i nærheden</h4>
                  <p className="text-stone-600 text-xs mt-1 leading-relaxed">
                    Kolby Mølle, Brundby Stubmølle, Falkecenter Samsø i Pillemark, islandske heste ved Nørreskiftegaard, Fredensdal husmandssted, Samsø Tekniske Museum, Vesborg Fyr og Brattingsborg Gods.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-[#67aedd]/10 text-[#67aedd] p-3 rounded-xl">
                  <i className="fa-solid fa-utensils text-lg" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-[#0f0d0e]">Spisesteder</h4>
                  <p className="text-stone-600 text-xs mt-1 leading-relaxed">
                    Favn (Restaurant & Proviant), Himmel og Havn - begge beliggende på havnen i Kolby Kås.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-4 justify-center lg:justify-start">
              <Link
                to="/kontakt"
                className="bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-6 py-3 rounded-full shadow-sm transition-all duration-300 text-sm flex items-center gap-2"
              >
                <i className="fa-solid fa-envelope" /> Kontakt os
              </Link>
              <Link
                to="/om-campen"
                className="bg-white hover:bg-stone-50 text-stone-800 border border-stone-200 font-bold px-6 py-3 rounded-full shadow-sm transition-all duration-300 text-sm flex items-center gap-2"
              >
                <i className="fa-solid fa-circle-info" /> Om Campen Camping
              </Link>
            </div>
          </div>

          <SiteMapViewer 
            pdfUrl="/_assets/campen-camping-wayfinding-revideret-20-08.pdf"
            className="aspect-16/10 lg:aspect-square"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-b from-stone-50 to-[#e8f3fa] text-stone-800 py-16 text-center relative overflow-hidden border-t border-stone-200/60">
        <div className="absolute inset-0 z-0 opacity-5">
          <img
            src="https://assets.auxodev.dk/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/hyt14-scaled.jpg"
            alt="Campen Camping"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-6">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">
            Klar til at opleve den rolige Samsø-stemning?
          </h2>
          <p className="text-stone-600 text-sm md:text-base max-w-2xl mx-auto mb-8 leading-relaxed">
            Hvad enten du vil booke en hytte, leje hele pladsen eller blot har spørgsmål, står vi klar til at hjælpe dig.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/feriehuse"
              className="bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-8 py-3.5 rounded-full shadow-md hover:shadow-lg transition-all duration-300 text-sm"
            >
              <i className="fa-solid fa-house-chimney mr-2" /> Se alle enheder
            </Link>
            <Link
              to="/kontakt"
              className="bg-white hover:bg-stone-50 text-stone-800 border border-stone-200 font-semibold px-8 py-3.5 rounded-full transition-all duration-300 text-sm shadow-sm"
            >
              Kontakt os
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
